// Fachlogik der Freelancer-Verwaltung (ERP/CRM-Plan, Abschnitt 7).
//
// Hier steht ausschliesslich, was sich OHNE Datenbank entscheiden laesst:
// Wann ist ein Nachweis abgelaufen? Wer darf ueberhaupt eine Begehung
// uebernehmen? Welcher Freelancer passt zu welcher Baustelle? Der unreine
// Teil (Laden, Speichern, Verortung, Dokumente) liegt in freelancerService.ts.
//
// Kein jsdom im Projekt (Standing Decision) — deshalb pur und mit Vitest
// geprueft. Die Haversine-Formel wird NICHT erneut ausgeschrieben, sondern aus
// nearbyProjects.ts uebernommen: eine zweite Kopie waere die dritte im Repo
// (GlobalMapDashboard hat bereits eine, die dort bewusst stehen bleibt).
//
// Tabellen + RLS: supabase/freelancers.sql (MIGRATIONS.md Nr. 18).

import { calculateDistanceKm } from './nearbyProjects';

// ─── Typen (camelCase-Spiegel der Tabellen) ─────────────────────────────────

/** Lebenszyklus eines Freelancers. Spiegel des CHECK in freelancers.sql. */
export type FreelancerStatus = 'aktiv' | 'pausiert' | 'gesperrt';

/** Nachweisarten. Spiegel des CHECK in freelancers.sql. */
export type QualiTyp = 'rab30_b' | 'rab30_c' | 'arbeitsschutz' | 'haftpflicht' | 'sonstiges';

export interface Freelancer {
    id: string;
    companyId: string;
    /** In Phase D1 durchgehend undefined — der Login folgt erst in D3. */
    profileUserId?: string;
    name: string;
    firma?: string;
    street?: string;
    zip?: string;
    city?: string;
    lat?: number;
    lng?: number;
    /** Wie weit fahren? 1–500 km (CHECK in der Datenbank). */
    einsatzradiusKm: number;
    /** Vergütung je Begehung in CENT — nie in Euro rechnen. */
    satzJeBegehungCents?: number;
    /** Basispunkte: 1900 = 19 %, 0 = Kleinunternehmer (§19 UStG). */
    steuersatzBp: number;
    iban?: string;
    ustIdnr?: string;
    steuernummer?: string;
    datevKreditorNr?: string;
    status: FreelancerStatus;
    /** 1–5, eine Nachkommastelle-genau in der Datenbank (numeric(3,2)). */
    rating?: number;
    notes?: string;
    createdAt: string;
}

export interface FreelancerQualification {
    id: string;
    freelancerId: string;
    typ: QualiTyp;
    bezeichnung?: string;
    /** ISO-Datum (YYYY-MM-DD). Fehlt = unbefristet oder ungepflegt. */
    gueltigBis?: string;
    /** Objektpfad im privaten Bucket; nie eine URL. */
    dokumentPfad?: string;
}

// ─── Vokabular ──────────────────────────────────────────────────────────────

/** Reihenfolge = Reihenfolge in Auswahl und Tabelle. */
export const QUALI_TYPEN: QualiTyp[] = [
    'rab30_b',
    'rab30_c',
    'arbeitsschutz',
    'haftpflicht',
    'sonstiges',
];

/** Anzeigetexte. `Record` erzwingt einen Eintrag je Schluessel. */
export const QUALI_TYP_LABELS: Record<QualiTyp, string> = {
    rab30_b: 'SiGeKo-Qualifikation RAB 30 Anlage B',
    rab30_c: 'SiGeKo-Qualifikation RAB 30 Anlage C',
    arbeitsschutz: 'Arbeitsschutz-Fachkunde',
    haftpflicht: 'Berufs-/Betriebshaftpflicht',
    sonstiges: 'Sonstiger Nachweis',
};

/** Kurzform fuer Tabellenspalten, in denen der volle Text nicht passt. */
export const QUALI_TYP_KURZ: Record<QualiTyp, string> = {
    rab30_b: 'RAB 30 B',
    rab30_c: 'RAB 30 C',
    arbeitsschutz: 'Arbeitsschutz',
    haftpflicht: 'Haftpflicht',
    sonstiges: 'Sonstiges',
};

export const STATUS_LABELS: Record<FreelancerStatus, string> = {
    aktiv: 'Aktiv',
    pausiert: 'Pausiert',
    gesperrt: 'Gesperrt',
};

/**
 * Ohne diese drei Nachweise gibt es keine Begehung (Plan 7,
 * „Compliance-Leitplanken"): die SiGeKo-Qualifikation nach RAB 30 Anlage B
 * UND C sowie eine laufende Haftpflicht.
 *
 * Die Arbeitsschutz-Fachkunde steht bewusst NICHT in dieser Liste. Sie ist
 * Voraussetzung fuer den Erwerb der RAB-30-Qualifikation und geht in ihr auf;
 * sie zusaetzlich als eigenen, ablaufenden Pflichtnachweis zu fuehren wuerde
 * dieselbe Anforderung doppelt sperren — einmal ueber RAB 30 B/C und einmal
 * ueber ein Papier, das typischerweise gar kein Ablaufdatum traegt. Sie bleibt
 * als erfassbarer Nachweis erhalten und wird in der Ampel angezeigt, blockiert
 * aber keine Zuweisung.
 */
export const pflichtQualiTypen: QualiTyp[] = ['rab30_b', 'rab30_c', 'haftpflicht'];

/** Ist der Typ ein Pflichtnachweis? */
export const istPflichtQuali = (typ: string): boolean =>
    (pflichtQualiTypen as string[]).includes(typ);

// ─── Ablauf-Ampel ───────────────────────────────────────────────────────────

/**
 * Zustand eines Nachweises:
 * - 'gueltig'    : Ablaufdatum liegt mehr als VORWARNUNG_TAGE in der Zukunft
 * - 'laeuft_ab'  : laeuft in den naechsten 60 Tagen ab (heute eingeschlossen)
 * - 'abgelaufen' : Ablaufdatum liegt in der Vergangenheit
 * - 'ohne_datum' : kein Ablaufdatum hinterlegt
 */
export type QualiStatus = 'gueltig' | 'laeuft_ab' | 'abgelaufen' | 'ohne_datum';

/**
 * Vorwarnzeit in Tagen. 60 Tage sind der Abstand, in dem sich eine
 * Versicherung verlaengern oder eine Fortbildung buchen laesst — kuerzer
 * gewarnt heisst in der Praxis: zu spaet gewarnt.
 */
export const VORWARNUNG_TAGE = 60;

export const QUALI_STATUS_LABELS: Record<QualiStatus, string> = {
    gueltig: 'gültig',
    laeuft_ab: 'läuft ab',
    abgelaufen: 'abgelaufen',
    ohne_datum: 'ohne Ablaufdatum',
};

/** Tailwind-Klassen der Ampel. Die UI waehlt nie selbst eine Farbe. */
export const QUALI_STATUS_KLASSEN: Record<QualiStatus, string> = {
    gueltig: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    laeuft_ab: 'bg-amber-50 text-amber-700 border-amber-200',
    abgelaufen: 'bg-rose-50 text-rose-700 border-rose-200',
    ohne_datum: 'bg-slate-50 text-slate-500 border-slate-200',
};

/** Heute als ISO-Datum in LOKALER Zeit ('sv-SE' liefert YYYY-MM-DD). */
export const heuteIso = (): string => new Date().toLocaleDateString('sv-SE');

/**
 * Ein ISO-Datum als Tageszahl seit der Epoche. `Date.UTC` statt `new Date(s)`,
 * damit die Zeitzone nicht mitrechnet: `new Date('2026-08-08')` ist Mitternacht
 * UTC und in Deutschland bereits der 8. um 02:00 — ein Vergleich zweier so
 * gebildeter Daten waere zeitzonenabhaengig um einen Tag verschoben.
 * null bei allem, was kein Datum ist.
 */
const tagesZahl = (iso?: string | null): number | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? '').trim());
    if (!m) return null;
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isFinite(ms)) return null;
    return Math.round(ms / 86_400_000);
};

/**
 * Verbleibende Tage bis zum Ablauf. Negativ = bereits abgelaufen,
 * null = kein (verwertbares) Datum.
 */
export const tageBisAblauf = (
    gueltigBis?: string | null,
    heute: string = heuteIso(),
): number | null => {
    const ende = tagesZahl(gueltigBis);
    const jetzt = tagesZahl(heute);
    if (ende === null || jetzt === null) return null;
    return ende - jetzt;
};

/**
 * Zustand eines Nachweises zum Stichtag.
 *
 * Der Stichtag wird UEBERGEBEN und nicht hier gebildet: dieselbe Liste muss in
 * einem Test denselben Zustand ergeben wie in der Oberflaeche, und ein
 * eingebautes `new Date()` machte jede Pruefung vom Kalender abhaengig.
 *
 * Der Ablauftag selbst zaehlt noch als gueltig — ein Nachweis „gueltig bis
 * 31.12." deckt den 31.12. ab. Er faellt aber in die Vorwarnung, weil er am
 * naechsten Tag wertlos ist.
 */
export const qualiStatus = (
    quali: Pick<FreelancerQualification, 'gueltigBis'> | null | undefined,
    heute: string = heuteIso(),
): QualiStatus => {
    const tage = tageBisAblauf(quali?.gueltigBis, heute);
    if (tage === null) return 'ohne_datum';
    if (tage < 0) return 'abgelaufen';
    if (tage <= VORWARNUNG_TAGE) return 'laeuft_ab';
    return 'gueltig';
};

/** Deckt der Nachweis den Stichtag ab? 'ohne_datum' gilt als unbefristet. */
export const istQualiGueltig = (
    quali: Pick<FreelancerQualification, 'gueltigBis'> | null | undefined,
    heute: string = heuteIso(),
): boolean => qualiStatus(quali, heute) !== 'abgelaufen';

// ─── Einsatzbereitschaft ────────────────────────────────────────────────────

export interface Einsatzbereitschaft {
    /** Darf eine Begehung uebernehmen. */
    bereit: boolean;
    /** Was blockiert — leer, wenn `bereit`. Deutsche Klartexte fuer die UI. */
    gruende: string[];
    /** Blockiert (noch) nicht, ist aber zu beachten: Nachweise laufen ab. */
    warnungen: string[];
    /** Pflichtnachweise, die gar nicht hinterlegt sind. */
    fehlendeTypen: QualiTyp[];
    /** Pflichtnachweise, die abgelaufen sind. */
    abgelaufeneTypen: QualiTyp[];
}

/**
 * Nachweise eines Freelancers nach Typ gruppieren. Mehrere Zeilen desselben
 * Typs sind zulaessig (alte und erneuerte Urkunde stehen nebeneinander) —
 * gewertet wird der SPAETESTE Ablauf, sonst blockierte die alte Urkunde den
 * Einsatz, obwohl die neue laengst vorliegt.
 */
const besterNachweis = (
    qualis: FreelancerQualification[],
    typ: QualiTyp,
): FreelancerQualification | null => {
    const treffer = qualis.filter((q) => q?.typ === typ);
    if (treffer.length === 0) return null;
    // 'ohne_datum' schlaegt jedes Datum: unbefristet ist die stärkste Zusage.
    const unbefristet = treffer.find((q) => tagesZahl(q.gueltigBis) === null);
    if (unbefristet) return unbefristet;
    return treffer.reduce((best, q) =>
        (tagesZahl(q.gueltigBis) ?? -Infinity) > (tagesZahl(best.gueltigBis) ?? -Infinity) ? q : best,
    );
};

/**
 * Darf dieser Freelancer eine Begehung uebernehmen — und wenn nicht, warum?
 *
 * Zwei Bedingungen, beide aus Plan 7:
 *  1. Status ist 'aktiv' (pausiert = vorübergehend nicht verfuegbar,
 *     gesperrt = dauerhaft nicht mehr beauftragen).
 *  2. ALLE Pflichtnachweise liegen vor und sind nicht abgelaufen.
 *     „Abgelaufene Nachweise blockieren neue Zuweisungen automatisch" — die
 *     Sperre ist bewusst hart und nicht per Klick uebergehbar.
 *
 * Nachweise, die demnaechst ablaufen, blockieren NICHT: sie sind heute gueltig.
 * Sie erscheinen als `warnungen`, damit der Innendienst nachfasst, bevor die
 * Sperre zuschlaegt.
 */
export const einsatzbereitschaft = (
    freelancer: Pick<Freelancer, 'status'> | null | undefined,
    qualis: FreelancerQualification[] | null | undefined,
    heute: string = heuteIso(),
): Einsatzbereitschaft => {
    const gruende: string[] = [];
    const warnungen: string[] = [];
    const fehlendeTypen: QualiTyp[] = [];
    const abgelaufeneTypen: QualiTyp[] = [];

    if (!freelancer) {
        return { bereit: false, gruende: ['Kein Stammsatz.'], warnungen, fehlendeTypen, abgelaufeneTypen };
    }
    if (freelancer.status !== 'aktiv') {
        gruende.push(`Status „${STATUS_LABELS[freelancer.status] ?? freelancer.status}" — nicht aktiv.`);
    }

    const liste = (qualis || []).filter(Boolean);
    for (const typ of pflichtQualiTypen) {
        const nachweis = besterNachweis(liste, typ);
        if (!nachweis) {
            fehlendeTypen.push(typ);
            gruende.push(`Pflichtnachweis fehlt: ${QUALI_TYP_LABELS[typ]}.`);
            continue;
        }
        const status = qualiStatus(nachweis, heute);
        if (status === 'abgelaufen') {
            abgelaufeneTypen.push(typ);
            gruende.push(`Abgelaufen: ${QUALI_TYP_LABELS[typ]} (${nachweis.gueltigBis}).`);
            continue;
        }
        if (status === 'laeuft_ab') {
            const tage = tageBisAblauf(nachweis.gueltigBis, heute) ?? 0;
            warnungen.push(
                `${QUALI_TYP_LABELS[typ]} läuft in ${tage} ${tage === 1 ? 'Tag' : 'Tagen'} ab (${nachweis.gueltigBis}).`,
            );
        }
    }

    return { bereit: gruende.length === 0, gruende, warnungen, fehlendeTypen, abgelaufeneTypen };
};

/** Kurzform von `einsatzbereitschaft` fuer Stellen, die nur das Ja/Nein brauchen. */
export const istEinsatzbereit = (
    freelancer: Pick<Freelancer, 'status'> | null | undefined,
    qualis: FreelancerQualification[] | null | undefined,
    heute: string = heuteIso(),
): boolean => einsatzbereitschaft(freelancer, qualis, heute).bereit;

// ─── Matching ───────────────────────────────────────────────────────────────

/** Das Projekt, fuer das gesucht wird. Nur die Felder, die zaehlen. */
export interface MatchProjekt {
    id?: string;
    name?: string;
    lat?: number | null;
    lng?: number | null;
}

export interface FreelancerMatch {
    freelancer: Freelancer;
    /** Luftlinie Projekt→Wohnort in km; null, wenn eine Seite nicht verortet ist. */
    distanzKm: number | null;
    /** Liegt das Projekt im hinterlegten Einsatzradius? null ohne Entfernung. */
    imRadius: boolean | null;
    bereitschaft: Einsatzbereitschaft;
    /** Warum nicht vorgeschlagen. Leer bei Vorschlaegen. */
    gruende: string[];
}

export interface FreelancerVorschlaege {
    /** Einsatzbereit UND im Radius, naechster zuerst. */
    vorschlaege: FreelancerMatch[];
    /** Alle uebrigen — jeweils mit Begruendung, nie stillschweigend weggelassen. */
    ausgeschlossen: FreelancerMatch[];
    /** Das Projekt hat keine Koordinaten — dann ist keine Entfernung moeglich. */
    projektUnverortet: boolean;
}

/**
 * Wer kommt fuer die Begehungen dieses Projekts in Frage (Plan 7, „Karte +
 * Matching")?
 *
 * Ergebnis ist bewusst ZWEITEILIG. Eine reine Trefferliste beantwortet die
 * Frage „warum steht Herr M. nicht dabei?" nicht — und genau die stellt der
 * Innendienst, wenn jemand fehlt, den er erwartet hat. Deshalb traegt jeder
 * Ausgeschlossene seinen Grund mit.
 *
 * Ist das Projekt selbst nicht verortet, kann niemand nach Entfernung sortiert
 * werden. Dann bleibt `vorschlaege` LEER statt willkuerlich gefuellt: eine
 * Reihenfolge vorzutaeuschen, die auf nichts beruht, waere schlechter als
 * keine. Die einsatzbereiten Kraefte stehen dann unter `ausgeschlossen` mit
 * dem Hinweis auf die fehlende Projektadresse.
 *
 * `qualis` ist eine FLACHE Liste ueber alle Freelancer (so kommt sie aus einer
 * einzigen Abfrage) und wird hier gruppiert.
 */
export const suggestFreelancers = (
    projekt: MatchProjekt | null | undefined,
    freelancers: Freelancer[] | null | undefined,
    qualis: FreelancerQualification[] | null | undefined,
    heute: string = heuteIso(),
): FreelancerVorschlaege => {
    const pLat = Number(projekt?.lat);
    const pLng = Number(projekt?.lng);
    const projektVerortet =
        Number.isFinite(pLat) && Number.isFinite(pLng) && !(pLat === 0 && pLng === 0);

    const nachFreelancer = new Map<string, FreelancerQualification[]>();
    for (const q of qualis || []) {
        if (!q?.freelancerId) continue;
        const liste = nachFreelancer.get(q.freelancerId);
        if (liste) liste.push(q);
        else nachFreelancer.set(q.freelancerId, [q]);
    }

    const vorschlaege: FreelancerMatch[] = [];
    const ausgeschlossen: FreelancerMatch[] = [];

    for (const f of freelancers || []) {
        if (!f?.id) continue;
        const bereitschaft = einsatzbereitschaft(f, nachFreelancer.get(f.id) ?? [], heute);

        const fLat = Number(f.lat);
        const fLng = Number(f.lng);
        const freelancerVerortet =
            Number.isFinite(fLat) && Number.isFinite(fLng) && !(fLat === 0 && fLng === 0);

        const distanzKm =
            projektVerortet && freelancerVerortet
                ? calculateDistanceKm(pLat, pLng, fLat, fLng)
                : null;
        const radius = Number.isFinite(f.einsatzradiusKm) ? f.einsatzradiusKm : 0;
        const imRadius = distanzKm === null ? null : distanzKm <= radius;

        const gruende = [...bereitschaft.gruende];
        if (!projektVerortet) {
            gruende.push('Das Projekt ist nicht verortet — Entfernung nicht berechenbar.');
        } else if (!freelancerVerortet) {
            gruende.push('Keine Koordinaten hinterlegt — Adresse verorten.');
        } else if (imRadius === false) {
            gruende.push(
                `Außerhalb des Einsatzradius (${formatDistanz(distanzKm as number)} bei ${radius} km).`,
            );
        }

        const match: FreelancerMatch = { freelancer: f, distanzKm, imRadius, bereitschaft, gruende };
        if (gruende.length === 0) vorschlaege.push(match);
        else ausgeschlossen.push(match);
    }

    // Naechster zuerst. Bei gleicher Entfernung entscheidet die bessere
    // Bewertung, danach der Name — damit die Liste stabil bleibt und nicht bei
    // jedem Laden anders sortiert ist.
    vorschlaege.sort((a, b) => {
        const da = a.distanzKm ?? Infinity;
        const db = b.distanzKm ?? Infinity;
        if (da !== db) return da - db;
        const ra = a.freelancer.rating ?? 0;
        const rb = b.freelancer.rating ?? 0;
        if (ra !== rb) return rb - ra;
        return a.freelancer.name.localeCompare(b.freelancer.name, 'de');
    });

    // Ausgeschlossene ebenfalls nach Naehe, damit „knapp ausserhalb" oben
    // steht und „400 km weg" unten.
    ausgeschlossen.sort((a, b) => (a.distanzKm ?? Infinity) - (b.distanzKm ?? Infinity));

    return { vorschlaege, ausgeschlossen, projektUnverortet: !projektVerortet };
};

// ─── Formatierung ───────────────────────────────────────────────────────────

/** Entfernung deutsch ("12,3 km"). Gleiche Form wie nearbyProjects. */
export const formatDistanz = (km: number): string => `${km.toFixed(1).replace('.', ',')} km`;

/** Cent-Betrag deutsch als Euro ("120,00 €"). '' bei fehlendem Wert. */
export const formatCents = (cents?: number | null): string =>
    Number.isFinite(cents as number)
        ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
              (cents as number) / 100,
          )
        : '';

/** Steuersatz aus Basispunkten ("19 %", "0 % (Kleinunternehmer)"). */
export const formatSteuersatz = (bp?: number | null): string => {
    const wert = Number(bp);
    if (!Number.isFinite(wert)) return '';
    if (wert === 0) return '0 % (Kleinunternehmer §19 UStG)';
    return `${(wert / 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
};

/** Anschrift einzeilig ("Musterstr. 1, 21079 Hamburg"); '' ohne Angaben. */
export const formatAnschrift = (
    f: Pick<Freelancer, 'street' | 'zip' | 'city'> | null | undefined,
): string =>
    [
        (f?.street || '').trim(),
        [(f?.zip || '').trim(), (f?.city || '').trim()].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(', ');
